import { BiomeContent } from '../data';
import { EventBus, EVENT_TYPES } from '../core/EventBus';
import { createLogger, Logger } from '../utils/Logger';

export interface MapPosition {
  readonly x: number;
  readonly y: number;
}

export interface PositionProvider {
  getPosition(): MapPosition;
}

export interface BiomeBackgroundRenderer {
  applyBiomeColors(backgroundColor: string, gridColor: string): void;
}

export interface MapSystemConfig {
  readonly eventBus: EventBus;
  readonly biomes: readonly BiomeContent[];
  readonly positionProvider: PositionProvider;
  readonly backgroundRenderer: BiomeBackgroundRenderer;
  readonly transitionDurationMs?: number;
  readonly logger?: Logger;
}

interface BiomeTransition {
  readonly previousBiomeId: string;
  readonly targetBiome: BiomeContent;
  readonly fromBackgroundColor: string;
  readonly fromGridColor: string;
  elapsedMs: number;
}

const DEFAULT_TRANSITION_DURATION_MS = 750;

export class MapSystem {
  private readonly eventBus: EventBus;
  private readonly biomes: readonly BiomeContent[];
  private readonly positionProvider: PositionProvider;
  private readonly backgroundRenderer: BiomeBackgroundRenderer;
  private readonly transitionDurationMs: number;
  private readonly initialPosition: MapPosition;
  private readonly logger: Logger;
  private currentBiome: BiomeContent;
  private renderedBackgroundColor: string;
  private renderedGridColor: string;
  private transition: BiomeTransition | null = null;

  constructor(config: MapSystemConfig) {
    this.logger = config.logger ?? createLogger('MapSystem');
    this.logger.debug('constructor:start', {
      biomeCount: config.biomes.length,
      transitionDurationMs: config.transitionDurationMs ?? DEFAULT_TRANSITION_DURATION_MS
    });

    if (config.biomes.length === 0) {
      this.logger.error('constructor:missing-biomes');
      throw new Error('MapSystem requires at least one biome');
    }

    this.eventBus = config.eventBus;
    this.biomes = [...config.biomes].sort(
      (left, right) => resolveDistanceThreshold(left) - resolveDistanceThreshold(right)
    );
    this.positionProvider = config.positionProvider;
    this.backgroundRenderer = config.backgroundRenderer;
    this.transitionDurationMs = Math.max(0, config.transitionDurationMs ?? DEFAULT_TRANSITION_DURATION_MS);
    this.initialPosition = clonePosition(config.positionProvider.getPosition());
    this.currentBiome = this.resolveBiomeForPosition(this.initialPosition);
    this.renderedBackgroundColor = this.currentBiome.backgroundColor;
    this.renderedGridColor = this.currentBiome.gridColor;
    this.backgroundRenderer.applyBiomeColors(this.renderedBackgroundColor, this.renderedGridColor);

    this.logger.info('constructor:complete', {
      initialPosition: this.initialPosition,
      initialBiomeId: this.currentBiome.id,
      biomeIds: this.biomes.map(biome => biome.id)
    });
  }

  public update(deltaMs: number): void {
    if (this.transition) {
      this.updateTransition(deltaMs);
      return;
    }

    const nextBiome = this.resolveBiomeForPosition(this.positionProvider.getPosition());
    if (nextBiome.id !== this.currentBiome.id) {
      this.startTransition(nextBiome);
      this.updateTransition(deltaMs);
    }

  }

  public getCurrentBiome(): BiomeContent {
    return this.currentBiome;
  }

  public getCurrentBiomeId(): string {
    return this.currentBiome.id;
  }

  public resolveBiomeForPosition(position: MapPosition): BiomeContent {
    const distance = calculateDistance(this.initialPosition, position);
    let selected = this.biomes[0] as BiomeContent;

    for (const biome of this.biomes) {
      if (distance >= resolveDistanceThreshold(biome)) {
        selected = biome;
      }
    }

    return selected;
  }

  public interpolateBiomeColors(fromBiome: BiomeContent, toBiome: BiomeContent, progress: number): {
    readonly backgroundColor: string;
    readonly gridColor: string;
  } {
    const boundedProgress = clamp(progress, 0, 1);
    const result = {
      backgroundColor: interpolateHexColor(fromBiome.backgroundColor, toBiome.backgroundColor, boundedProgress),
      gridColor: interpolateHexColor(fromBiome.gridColor, toBiome.gridColor, boundedProgress)
    };

    this.logger.debug('interpolateBiomeColors:complete', {
      fromBiomeId: fromBiome.id,
      toBiomeId: toBiome.id,
      progress: boundedProgress,
      result
    });
    return result;
  }

  private startTransition(nextBiome: BiomeContent): void {
    this.logger.info('biome:transition-started', {
      previousBiomeId: this.currentBiome.id,
      nextBiomeId: nextBiome.id,
      transitionDurationMs: this.transitionDurationMs
    });

    this.transition = {
      previousBiomeId: this.currentBiome.id,
      targetBiome: nextBiome,
      fromBackgroundColor: this.renderedBackgroundColor,
      fromGridColor: this.renderedGridColor,
      elapsedMs: 0
    };

    if (this.transitionDurationMs === 0) {
      this.completeTransition();
    }
  }

  private updateTransition(deltaMs: number): void {
    if (!this.transition) return;

    this.transition.elapsedMs += Math.max(0, Number.isFinite(deltaMs) ? deltaMs : 0);
    const progress = this.transitionDurationMs === 0
      ? 1
      : clamp(this.transition.elapsedMs / this.transitionDurationMs, 0, 1);

    this.renderedBackgroundColor = interpolateHexColor(
      this.transition.fromBackgroundColor,
      this.transition.targetBiome.backgroundColor,
      progress
    );
    this.renderedGridColor = interpolateHexColor(
      this.transition.fromGridColor,
      this.transition.targetBiome.gridColor,
      progress
    );
    this.backgroundRenderer.applyBiomeColors(this.renderedBackgroundColor, this.renderedGridColor);

    if (progress >= 1) {
      this.completeTransition();
    }
  }

  private completeTransition(): void {
    if (!this.transition) return;

    const previousBiomeId = this.transition.previousBiomeId;
    this.currentBiome = this.transition.targetBiome;
    this.renderedBackgroundColor = this.currentBiome.backgroundColor;
    this.renderedGridColor = this.currentBiome.gridColor;
    this.backgroundRenderer.applyBiomeColors(this.renderedBackgroundColor, this.renderedGridColor);
    this.transition = null;

    this.logger.info('biome:changed', {
      previousBiomeId,
      currentBiomeId: this.currentBiome.id
    });
    this.eventBus.publish({
      type: EVENT_TYPES.BIOME_CHANGED,
      data: {
        previousBiomeId,
        currentBiomeId: this.currentBiome.id
      }
    });
  }
}

function resolveDistanceThreshold(biome: BiomeContent): number {
  return biome.distanceThreshold ?? 0;
}

function calculateDistance(from: MapPosition, to: MapPosition): number {
  return Math.hypot(to.x - from.x, to.y - from.y);
}

function clonePosition(position: MapPosition): MapPosition {
  return { x: position.x, y: position.y };
}

function interpolateHexColor(from: string, to: string, progress: number): string {
  const fromRgb = parseHexColor(from);
  const toRgb = parseHexColor(to);
  return formatHexColor({
    r: interpolateChannel(fromRgb.r, toRgb.r, progress),
    g: interpolateChannel(fromRgb.g, toRgb.g, progress),
    b: interpolateChannel(fromRgb.b, toRgb.b, progress)
  });
}

function parseHexColor(color: string): { readonly r: number; readonly g: number; readonly b: number } {
  const normalized = color.startsWith('#') ? color.slice(1) : color;
  return {
    r: Number.parseInt(normalized.slice(0, 2), 16),
    g: Number.parseInt(normalized.slice(2, 4), 16),
    b: Number.parseInt(normalized.slice(4, 6), 16)
  };
}

function formatHexColor(color: { readonly r: number; readonly g: number; readonly b: number }): string {
  return `#${formatChannel(color.r)}${formatChannel(color.g)}${formatChannel(color.b)}`;
}

function interpolateChannel(from: number, to: number, progress: number): number {
  return Math.round(from + (to - from) * progress);
}

function formatChannel(value: number): string {
  return clamp(value, 0, 255).toString(16).padStart(2, '0');
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
